import {
  epochMillisecondsFromHttpDate,
  nowEpochMilliseconds,
} from "@nijiurachan/js/util/time";
import { useSyncExternalStore } from "react";

const deadlines = new Map<string, number>();
export const POLL_CREATION_COOLDOWN_KEY = "post-polls-v1";
const listeners = new Set<() => void>();
let revision = 0;
let timer: number | null = null;

function notify(): void {
  revision += 1;
  for (const listener of listeners) listener();
}

function getSnapshot(): number {
  return revision;
}

function stopTimerIfIdle(): void {
  if (deadlines.size !== 0 || timer === null) return;
  window.clearInterval(timer);
  timer = null;
}

function tick(): void {
  const now = nowEpochMilliseconds();
  for (const [key, deadline] of deadlines) {
    if (deadline <= now) deadlines.delete(key);
  }
  notify();
  stopTimerIfIdle();
}

function ensureTimer(): void {
  if (timer !== null || typeof window === "undefined") return;
  timer = window.setInterval(tick, 1_000);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}

export function parseRetryAfterDeadline(
  retryAfter: string | undefined,
  currentEpochMilliseconds: number = nowEpochMilliseconds(),
): number | null {
  if (!retryAfter) return null;
  const trimmed = retryAfter.trim();
  if (/^\d+$/u.test(trimmed)) {
    return currentEpochMilliseconds + Number(trimmed) * 1_000;
  }
  const parsed = epochMillisecondsFromHttpDate(trimmed);
  return parsed !== null && parsed > currentEpochMilliseconds ? parsed : null;
}

export function recordPollCreationCooldown(
  key: string,
  retryAfter: string | undefined,
): number | null {
  const deadline = parseRetryAfterDeadline(retryAfter);
  if (deadline === null) return null;
  deadlines.set(key, deadline);
  ensureTimer();
  notify();
  return deadline;
}

export function usePollCreationCooldownSeconds(key: string): number {
  const currentRevision = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getSnapshot,
  );
  void currentRevision;
  const deadline = deadlines.get(key);
  return deadline === undefined
    ? 0
    : Math.max(0, Math.ceil((deadline - nowEpochMilliseconds()) / 1_000));
}

export function resetPollCreationCooldownForTest(): void {
  deadlines.clear();
  if (timer !== null && typeof window !== "undefined") {
    window.clearInterval(timer);
    timer = null;
  }
  notify();
}
